<template>
    <tr>
      <td class="cell-download">
        <a href="#" @click.stop.prevent="download()" download>
          <font-awesome-icon icon="spinner" spin v-show="isDownloading"  />
          <font-awesome-icon icon="file-download" v-show="!isDownloading" />
          <div class="is-size-7">download</div>
        </a>
      </td>
      <td>
        <a href="#" @click.stop.prevent="open()">
          {{ document.documentName }}
        </a>
        <pdf-viewer :show.sync="showPdfViewer" :file-path="dataUri" :file-name="fullDetails.documentName" />
        <image-viewer :show.sync="showImageViewer" :file-path="dataUri" :file-name="fullDetails.documentName" />
      </td>
      <td v-if="showUpdated"> {{ formattedDate }} </td>
      <td> {{ documentTypeMap[document.documentType] }} </td>
      <td v-if="canDelete">
        <button class="button is-danger"
                :disabled="isSystemGenerated"
                :class="{ 'is-loading': isDeleting }"
                type="button"
                @click="deleteDoc(document.documentKey)">
          <font-awesome-icon icon="times" />
        </button>
      </td>
    </tr>
</template>

<script>
import { format } from 'date-fns'
import DocumentApi from '@/api-interface/document'
import { last, isEmpty } from 'lodash-es'
import fileDownload from '@/helpers/downloadBlob'
import documentTypeMap from '@/lookup-data/document-types'
import PdfViewer from '@/components/ModalDisplayPdf'
import ImageViewer from '@/components/ModalDisplayImages'

export default {
  name: 'CompanyDocumentsTable-Row',
  model: {
    prop: 'document',
    event: 'input'
  },
  props: {
    document: {
      type: Object,
      required: true
    },
    canDelete: {
      type: Boolean,
      default: true
    },
    showUpdated: {
      type: Boolean,
      default: true
    }
  },
  components: {
    PdfViewer,
    ImageViewer
  },

  computed: {
    isPdf() {
      const extension = last(this.document.documentName.split('.')).toLowerCase()
      return extension === 'pdf'
    },

    isImage() {
      const imageExtensions = ['jpg', 'jpeg', 'gif', 'png', 'bmp']
      const extension = last(this.document.documentName.split('.')).toLowerCase()
      return imageExtensions.includes(extension)
    },

    isSystemGenerated() {
      return this.document.documentType === 'blockchain_file'
    },

    formattedDate() {
      return format(this.document.updatedTimestamp, 'MMM DD, YYYY')
    }
  },

  data() {
    return {
      documentTypeMap,
      isDeleting: false,
      isDownloading: false,
      showPdfViewer: false,
      showImageViewer: false,
      dataUri: '',
      fullDetails: {}
    }
  },

  methods: {
    async fetchDetails() {
      this.isDownloading = true
      try {
        this.fullDetails = await DocumentApi.getByKey(this.document.documentKey)
      } catch (error) {
        console.error(`Error downloading document ${this.document.documentKey} : `, error)
      } finally {
        this.isDownloading = false
      }
    },

    async download() {
      if (isEmpty(this.fullDetails)) {
        await this.fetchDetails()
      }
      fileDownload({ fileString: this.fullDetails.documentData, userFriendlyName: this.fullDetails.documentName })
    },

    async open() {
      if (isEmpty(this.fullDetails)) {
        await this.fetchDetails()
      }

      // PDF files get displayed in-line, all other file types are downloaded
      if (this.isPdf) {
        this.showPdfViewer = true
        this.dataUri = `data:application/pdf;base64,${this.fullDetails.documentData}`
      } else if (this.isImage) {
        this.showImageViewer = true
        this.dataUri = `data:application/octet-stream;base64,${this.fullDetails.documentData}`
      } else {
        this.download()
      }
    },

    async deleteDoc() {
      this.isDeleting = true
      try {
        await DocumentApi.delete(this.document.documentKey)
        // give the backend a chance to complete the delete before notifying the parent
        window.setTimeout(() => {
          this.$emit('deleted')
        }, 500)
      } catch (error) {
        this.isDeleting = false
        console.error(`Error deleting document ${this.document.documentKey} : `, error)
      }
    }
  }
}
</script>

<style scoped>
.cell-download {
  text-align: center;
}
</style>
